import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useRef, useState } from "react";
import { functions } from "../firebase";
export const templateLabels = {
    source: "Source",
    account: "Existing account",
    allSources: "All sources",
    status: "Status",
    allStatuses: "All statuses",
    allLanguages: "All languages",
    clearFilters: "Clear filters",
    noMatches: "No templates match these filters.",
    results: "Results",
    accountHelp: "Existing account templates are managed in Meta and are view-only here.",
    accountLoading: "Loading existing account templates…",
    accountLoadError: "Existing account templates could not be loaded. Refresh to try again.",
    authentication: "Authentication",
    responsesActive: "External reply relay active for selected test numbers.",
    responseLease: "Reply handoff expires",
    anotherTest: "Prepare another test message",
    borrowed: "Borrowed WhatsApp account",
    borrowHelp: "This connection uses externally supplied credentials. Connection changes and automatic replies are disabled.",
    expiry: "Expires",
    sendTest: "Send one template message",
    recipient: "Allowed test recipient",
    variables: "Variables, one per line",
    send: "Send test message",
    sending: "Sending…",
    sent: "Meta accepted the message.",
    sendFailed: "The message was not sent.",
    sendUnknown: "Send outcome is unknown. This request will not be resent automatically.",
    selectTemplate: "Approved template",
    title: "WhatsApp templates",
    global: "Global",
    company: "Company",
    search: "Search templates",
    create: "New template",
    refresh: "Refresh status",
    retry: "Register missing / Retry",
    save: "Save and submit",
    cancel: "Cancel",
    edit: "Edit",
    archive: "Archive",
    archiveConfirm: "Archive this template? Existing Meta registrations will be retained.",
    enable: "Enable for this company",
    automatic: "Register automatically",
    automaticHelp: "Register this template for every connected company and on future connections.",
    registeredAutomatically: "Registered automatically",
    name: "Title",
    language: "Language code",
    category: "Category",
    utility: "Utility",
    marketing: "Marketing",
    body: "Message body",
    samples: "Variable examples, one per line",
    preview: "Preview",
    empty: "No templates to display.",
    disconnected: "Connect WhatsApp to register templates. You can prepare templates now.",
    loading: "Loading…",
    notEnabled: "Not enabled",
    queued: "Waiting for registration",
    previous: "Previous version remains approved.",
    revision: "Revision",
    checked: "Last checked",
    error: "Unable to complete the request. Try again.",
    approved: "Approved",
    pending: "Under review",
    rejected: "Rejected",
    failed: "Registration failed",
    submitting: "Submitting",
    canceled: "Canceled",
    paused: "Paused",
    disabled: "Disabled",
    readOnly: "View only",
    close: "Close",
    saved: "Saved. Applicable registrations will run in the background.",
    unknown: "Status unavailable",
    languageHelp: "Use a Meta language code, for example en_US or he.",
    policyHelp: "Global templates are managed by system administrators.",
    archived: "Archived",
};
const empty = {
    title: "",
    language: "en_US",
    category: "UTILITY",
    body: "",
    samples: [],
    automatic: false,
};
async function call(name, payload) {
    return (await httpsCallable(functions, name)(payload)).data;
}
export function WhatsAppTemplateCatalog({ companyId = null, canManage = false, labels = templateLabels, }) {
    const modal = useRef(null);
    const l = labels;
    const [data, setData] = useState();
    const [source, setSource] = useState("all");
    const [status, setStatus] = useState("all");
    const [language, setLanguage] = useState("all");
    const [accountTemplates, setAccountTemplates] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    const [accountError, setAccountError] = useState(false);
    const [accountCheckedAt, setAccountCheckedAt] = useState(null);
    const accountRequest = useRef(0);
    const accountCompany = useRef(companyId);
    accountCompany.current = companyId;
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [form, setForm] = useState(null);
    const [testSend, setTestSend] = useState({
        registrationId: "",
        recipient: "",
        parameters: "",
        requestId: "",
    });
    const [sendResult, setSendResult] = useState("");
    const [view, setView] = useState(null);
    useEffect(() => {
        if ((form || view) && modal.current && !modal.current.open)
            modal.current.showModal();
    }, [form, view]);
    async function load() {
        const result = await call("whatsappTemplateCatalog", { companyId });
        setData(result);
    }
    useEffect(() => {
        let active = true;
        setData(undefined);
        call("whatsappTemplateCatalog", { companyId })
            .then((result) => {
            if (active)
                setData(result);
        })
            .catch(() => {
            if (active)
                setError(l.error);
        });
        return () => {
            active = false;
        };
    }, [companyId, l.error]);
    const loadAccount = useCallback(async () => {
        if (!companyId || accountCompany.current !== companyId)
            return;
        const sequence = ++accountRequest.current;
        setAccountLoading(true);
        setAccountError(false);
        try {
            const result = (await call("whatsappAccountTemplates", {
                companyId,
            }));
            if (sequence !== accountRequest.current ||
                accountCompany.current !== companyId)
                return;
            setAccountTemplates(result.templates);
            setAccountCheckedAt(result.checkedAt);
        }
        catch {
            if (sequence === accountRequest.current)
                setAccountError(true);
        }
        finally {
            if (sequence === accountRequest.current)
                setAccountLoading(false);
        }
    }, [companyId]);
    useEffect(() => {
        setAccountTemplates([]);
        setAccountCheckedAt(null);
        setAccountError(false);
        setAccountLoading(false);
        setSource("all");
        setStatus("all");
        setLanguage("all");
        if (companyId && data?.connected)
            void loadAccount();
        return () => {
            accountRequest.current++;
        };
    }, [companyId, data?.connected, loadAccount]);
    async function run(action, saved = false) {
        setBusy(true);
        setError("");
        setNotice("");
        try {
            await action();
            await load();
            if (saved)
                setNotice(l.saved);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : l.error);
        }
        finally {
            setBusy(false);
        }
    }
    const control = "rounded-md border border-input bg-card px-3 py-2 text-sm disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring";
    const primary = `${control} bg-primary text-primary-foreground`;
    const definitionStatus = (t) => {
        if (!companyId)
            return "";
        const enabled = !!t.companyId || t.automatic || data?.enrolled.includes(t.id);
        return !enabled
            ? "NOT_ENABLED"
            : data?.registrations.find((r) => r.templateId === t.id && r.revision === t.revision)?.status || "QUEUED";
    };
    const matches = (item, title, itemSource, itemStatus) => (source === "all" || source === itemSource) &&
        (status === "all" || status === itemStatus) &&
        (language === "all" || language === item.language) &&
        `${title} ${item.body} ${item.language}`
            .toLowerCase()
            .includes(search.trim().toLowerCase());
    const definitions = data?.templates.filter((t) => !t.archived) || [];
    const existing = accountTemplates;
    const items = definitions.filter((t) => matches(t, t.title, t.companyId ? "company" : "global", definitionStatus(t)));
    const accountItems = existing.filter((t) => matches(t, t.name, "account", t.status));
    const resultCount = items.length + accountItems.length;
    const totalCount = definitions.length + existing.length;
    const languages = [
        ...new Set([...definitions, ...existing].map((t) => t.language)),
    ].sort();
    const statuses = [
        ...new Set([
            ...definitions.map(definitionStatus),
            ...existing.map((t) => t.status),
        ].filter(Boolean)),
    ].sort();
    const filtered = !!search || source !== "all" || status !== "all" || language !== "all";
    const categoryLabel = (category) => ({
        UTILITY: l.utility,
        MARKETING: l.marketing,
        AUTHENTICATION: l.authentication,
    })[category] || category;
    const statusLabel = (status) => ({
        APPROVED: l.approved,
        PENDING: l.pending,
        REJECTED: l.rejected,
        ERROR: l.failed,
        SUBMITTING: l.submitting,
        CANCELED: l.canceled,
        PAUSED: l.paused,
        DISABLED: l.disabled,
        QUEUED: l.queued,
        NOT_ENABLED: l.notEnabled,
    })[status] || `${l.unknown} (${status})`;
    function editor(t) {
        setForm(t
            ? {
                id: t.id,
                title: t.title,
                language: t.language,
                category: t.category,
                body: t.body,
                samples: t.samples,
                automatic: t.automatic,
            }
            : { ...empty });
    }
    return (_jsxs("section", { className: "space-y-5 min-w-0", "aria-label": l.title, children: [data?.borrowed && (_jsxs("div", { className: "border border-border rounded-md bg-card text-card-foreground p-4 text-sm space-y-1", role: "status", children: [_jsxs("p", { className: "font-semibold", children: [l.borrowed, " \u00B7 ", data.borrowed.sourceProject] }), _jsx("p", { children: data.borrowed.active &&
                            data.borrowed.responsesEnabled &&
                            Date.parse(data.borrowed.responseLeaseExpiresAt || "") > Date.now()
                            ? l.responsesActive
                            : l.borrowHelp }), data.borrowed.responseLeaseExpiresAt && (_jsxs("p", { children: [l.responseLease, ":", " ", new Date(data.borrowed.responseLeaseExpiresAt).toLocaleString()] })), _jsxs("p", { children: [l.expiry, ": ", new Date(data.borrowed.expiresAt).toLocaleString()] })] })), _jsxs("div", { className: "space-y-5 rounded-md border border-border bg-card text-card-foreground p-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-xl font-semibold", children: l.title }), companyId && (_jsx("p", { className: "text-sm text-muted-foreground mt-1", children: l.policyHelp }))] }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [companyId && (_jsxs(_Fragment, { children: [_jsx("button", { className: control, disabled: busy || !data?.connected, onClick: () => run(async () => {
                                                    if (canManage)
                                                        await call("refreshWhatsAppTemplates", { companyId });
                                                    await loadAccount();
                                                }), children: l.refresh }), canManage && (_jsx("button", { className: control, disabled: busy || !data?.connected, onClick: () => run(() => call("refreshWhatsAppTemplates", {
                                                    companyId,
                                                    retry: true,
                                                })), children: l.retry }))] })), canManage && (_jsx("button", { className: primary, disabled: busy, onClick: () => editor(), children: l.create }))] })] }), error && (_jsx("p", { role: "alert", className: "rounded-md border p-3 text-sm text-destructive", children: error })), notice && (_jsx("p", { role: "status", className: "text-sm", children: notice })), companyId && data && !data.connected && (_jsx("p", { className: "border rounded-md p-3 text-sm", children: l.disconnected })), _jsxs("div", { className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-4", children: [_jsxs("label", { className: "block text-sm", children: [l.search, _jsx("input", { className: `${control} block w-full mt-1 placeholder:text-muted-foreground placeholder:opacity-100`, value: search, onChange: (e) => setSearch(e.target.value), placeholder: l.search })] }), companyId && (_jsxs("label", { className: "block text-sm", children: [l.source, _jsxs("select", { className: `${control} block w-full mt-1`, value: source, onChange: (e) => setSource(e.target.value), children: [_jsx("option", { value: "all", children: l.allSources }), _jsx("option", { value: "company", children: l.company }), _jsx("option", { value: "global", children: l.global }), _jsx("option", { value: "account", children: l.account })] })] })), companyId && (_jsxs("label", { className: "block text-sm", children: [l.status, _jsxs("select", { className: `${control} block w-full mt-1`, value: status, onChange: (e) => setStatus(e.target.value), children: [_jsx("option", { value: "all", children: l.allStatuses }), statuses.map((value) => (_jsx("option", { value: value, children: statusLabel(value) }, value)))] })] })), _jsxs("label", { className: "block text-sm", children: [l.language, _jsxs("select", { className: `${control} block w-full mt-1`, value: language, onChange: (e) => setLanguage(e.target.value), children: [_jsx("option", { value: "all", children: l.allLanguages }), languages.map((value) => (_jsx("option", { value: value, children: value }, value)))] })] })] })] }), data && (_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground", children: [_jsxs("p", { role: "status", children: [l.results, ": ", resultCount] }), filtered && (_jsx("button", { className: control, onClick: () => {
                            setSearch("");
                            setSource("all");
                            setStatus("all");
                            setLanguage("all");
                        }, children: l.clearFilters }))] })), accountLoading && (_jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: l.accountLoading })), accountError && (_jsx("p", { role: "alert", className: "text-sm text-destructive", children: l.accountLoadError })), accountItems.length > 0 && (_jsx("p", { className: "text-sm text-muted-foreground", children: l.accountHelp })), !data ? (_jsx("p", { role: "status", children: l.loading })) : resultCount === 0 ? (_jsx("p", { className: "py-10 text-center text-muted-foreground", children: accountLoading || (accountError && source === "account")
                    ? ""
                    : totalCount && filtered
                        ? l.noMatches
                        : l.empty })) : (_jsxs("ul", { className: "divide-y divide-border rounded-md border border-border bg-card text-card-foreground", children: [items.map((t) => {
                        const regs = data.registrations.filter((r) => r.templateId === t.id);
                        const latest = regs.find((r) => r.revision === t.revision);
                        const approved = regs.some((r) => r.status === "APPROVED" && r.revision < t.revision);
                        const enabled = !!t.companyId || t.automatic || data.enrolled.includes(t.id);
                        const editable = canManage && (!companyId || !!t.companyId);
                        return (_jsxs("li", { className: "p-4 flex flex-wrap items-start justify-between gap-4", children: [_jsxs("div", { className: "min-w-0 flex-1 basis-64", children: [_jsx("button", { className: "font-semibold text-start underline-offset-4 hover:underline break-words", onClick: () => setView(t), children: t.title }), _jsxs("p", { className: "text-xs text-muted-foreground mt-1", children: [t.companyId ? l.company : l.global, " \u00B7 ", t.language, " \u00B7", " ", categoryLabel(t.category), " \u00B7 ", l.revision, " ", t.revision] }), _jsx("p", { className: "text-sm mt-2 whitespace-pre-wrap break-words line-clamp-2", children: t.body }), companyId && (_jsx("p", { className: "text-sm mt-2 font-medium", children: !enabled
                                                ? l.notEnabled
                                                : latest
                                                    ? statusLabel(latest.status)
                                                    : l.queued })), companyId && t.automatic && (_jsx("p", { className: "text-xs mt-1 text-muted-foreground", children: l.registeredAutomatically })), !companyId && t.automatic && (_jsx("p", { className: "text-xs mt-1", children: l.automatic })), approved && latest?.status !== "APPROVED" && (_jsx("p", { className: "text-sm mt-1", children: l.previous })), latest?.error && (_jsx("p", { className: "text-sm text-destructive mt-1", role: "status", children: latest.error })), latest?.checkedAt && (_jsxs("p", { className: "text-xs text-muted-foreground mt-1", children: [l.checked, ": ", new Date(latest.checkedAt).toLocaleString()] }))] }), _jsxs("div", { className: "flex gap-2 flex-wrap", children: [companyId && canManage && !enabled && (_jsx("button", { className: control, disabled: busy, onClick: () => run(() => call("enableWhatsAppTemplate", {
                                                companyId,
                                                templateId: t.id,
                                            }), true), children: l.enable })), editable && (_jsxs(_Fragment, { children: [_jsx("button", { className: control, disabled: busy, onClick: () => editor(t), children: l.edit }), _jsx("button", { className: `${control} text-destructive`, disabled: busy, onClick: () => {
                                                        if (window.confirm(l.archiveConfirm))
                                                            run(() => call("saveWhatsAppTemplate", {
                                                                companyId,
                                                                id: t.id,
                                                                action: "archive",
                                                            }));
                                                    }, children: l.archive })] }))] })] }, t.id));
                    }), accountItems.map((t) => (_jsxs("li", { className: "p-4 flex flex-wrap items-start justify-between gap-4", children: [_jsxs("div", { className: "min-w-0 flex-1 basis-64", children: [_jsx("button", { className: "font-semibold text-start underline-offset-4 hover:underline break-all", dir: "auto", onClick: () => setView(t), children: t.name }), _jsxs("p", { className: "text-xs text-muted-foreground mt-1", children: [l.account, " \u00B7 ", t.language, " \u00B7 ", categoryLabel(t.category)] }), _jsx("p", { className: "text-sm mt-2 whitespace-pre-wrap break-words line-clamp-2", dir: "auto", children: t.body || t.header || t.headerFormat }), _jsx("p", { className: "text-sm mt-2 font-medium", children: statusLabel(t.status) }), accountCheckedAt && (_jsxs("p", { className: "text-xs text-muted-foreground mt-1", children: [l.checked, ": ", new Date(accountCheckedAt).toLocaleString()] }))] }), _jsx("span", { className: "text-sm text-muted-foreground", children: l.readOnly })] }, `account:${t.id}`)))] })), data?.borrowed && data.canSendTest && (_jsxs("form", { className: "border border-border rounded-md bg-card text-card-foreground p-4 space-y-3", onSubmit: (e) => {
                    e.preventDefault();
                    const requestId = testSend.requestId || crypto.randomUUID();
                    setTestSend({ ...testSend, requestId });
                    run(async () => {
                        const result = (await call("sendBorrowedWhatsAppTemplate", {
                            companyId,
                            registrationId: testSend.registrationId,
                            recipient: testSend.recipient,
                            parameters: testSend.parameters
                                ? testSend.parameters.split("\n")
                                : [],
                            requestId,
                        }));
                        setSendResult(result.status);
                    });
                }, children: [_jsx("h3", { className: "font-semibold", children: l.sendTest }), _jsxs("label", { className: "block text-sm", children: [l.selectTemplate, _jsxs("select", { required: true, className: `${control} block w-full mt-1`, value: testSend.registrationId, onChange: (e) => {
                                    setTestSend({
                                        ...testSend,
                                        registrationId: e.target.value,
                                        requestId: "",
                                    });
                                    setSendResult("");
                                }, children: [_jsx("option", { value: "", children: "\u2014" }), data.registrations
                                        .filter((r) => r.status === "APPROVED")
                                        .map((r) => (_jsxs("option", { value: r.id, children: [data.templates.find((t) => t.id === r.templateId)?.title, " \u00B7", " ", l.revision, " ", r.revision] }, r.id)))] })] }), _jsxs("label", { className: "block text-sm", children: [l.recipient, _jsx("input", { required: true, dir: "ltr", pattern: "\\+[1-9][0-9]{6,14}", className: `${control} block w-full mt-1`, value: testSend.recipient, onChange: (e) => {
                                    setTestSend({
                                        ...testSend,
                                        recipient: e.target.value,
                                        requestId: "",
                                    });
                                    setSendResult("");
                                } })] }), _jsxs("label", { className: "block text-sm", children: [l.variables, _jsx("textarea", { dir: "auto", className: `${control} block w-full mt-1`, value: testSend.parameters, onChange: (e) => {
                                    setTestSend({
                                        ...testSend,
                                        parameters: e.target.value,
                                        requestId: "",
                                    });
                                    setSendResult("");
                                } })] }), _jsx("button", { className: primary, disabled: busy || !!sendResult, children: busy ? l.sending : l.send }), sendResult && sendResult !== "unknown" && (_jsx("button", { type: "button", className: control, onClick: () => {
                            setSendResult("");
                            setTestSend({ ...testSend, requestId: "" });
                        }, children: l.anotherTest })), sendResult && (_jsx("p", { role: "status", children: sendResult === "sent"
                            ? l.sent
                            : sendResult === "failed"
                                ? l.sendFailed
                                : l.sendUnknown }))] })), (form || view) && (_jsxs("dialog", { ref: modal, onCancel: (e) => {
                    e.preventDefault();
                    if (!busy) {
                        setForm(null);
                        setView(null);
                    }
                }, "aria-label": form ? (form.id ? l.edit : l.create) : l.preview, className: "m-auto backdrop:bg-black/40 bg-card text-card-foreground rounded-lg shadow-xl p-5 w-[calc(100%-1.5rem)] max-w-4xl max-h-[90dvh] overflow-auto", children: [error && (_jsx("p", { role: "alert", className: "text-destructive mb-3", children: error })), _jsx("h3", { className: "font-semibold text-lg mb-4", children: form ? (form.id ? l.edit : l.create) : l.preview }), form ? (_jsxs("form", { onSubmit: (e) => {
                            e.preventDefault();
                            run(async () => {
                                await call("saveWhatsAppTemplate", { ...form, companyId });
                                if (!form.id) {
                                    setSearch("");
                                    setSource("all");
                                    setStatus("all");
                                    setLanguage("all");
                                }
                                setForm(null);
                            }, true);
                        }, children: [_jsxs("div", { className: "grid md:grid-cols-2 gap-6", children: [_jsxs("div", { className: "space-y-3", children: [_jsxs("label", { className: "block text-sm", children: [l.name, _jsx("input", { autoFocus: true, required: true, maxLength: 120, className: `${control} block w-full mt-1`, value: form.title, onChange: (e) => setForm({ ...form, title: e.target.value }) })] }), _jsxs("label", { className: "block text-sm", children: [l.language, _jsx("input", { required: true, pattern: "[a-z]{2,3}(_[A-Z]{2})?", className: `${control} block w-full mt-1`, value: form.language, onChange: (e) => setForm({ ...form, language: e.target.value }) }), _jsx("span", { className: "text-xs text-muted-foreground", children: l.languageHelp })] }), _jsxs("label", { className: "block text-sm", children: [l.category, _jsxs("select", { className: `${control} block w-full mt-1`, value: form.category, onChange: (e) => setForm({
                                                            ...form,
                                                            category: e.target.value,
                                                        }), children: [_jsx("option", { value: "UTILITY", children: l.utility }), _jsx("option", { value: "MARKETING", children: l.marketing })] })] }), _jsxs("label", { className: "block text-sm", children: [l.body, _jsx("textarea", { required: true, maxLength: 1024, rows: 5, className: `${control} block w-full mt-1`, value: form.body, onChange: (e) => setForm({ ...form, body: e.target.value }) })] }), _jsxs("label", { className: "block text-sm", children: [l.samples, _jsx("textarea", { rows: 3, className: `${control} block w-full mt-1`, value: form.samples.join("\n"), onChange: (e) => setForm({
                                                            ...form,
                                                            samples: e.target.value
                                                                ? e.target.value.split("\n")
                                                                : [],
                                                        }) })] }), !companyId && (_jsxs("label", { className: "block text-sm", children: [_jsx("input", { type: "checkbox", checked: form.automatic, onChange: (e) => setForm({ ...form, automatic: e.target.checked }) }), " ", l.automatic, _jsx("span", { className: "block text-xs text-muted-foreground mt-1", children: l.automaticHelp })] }))] }), _jsxs("div", { children: [_jsx("h4", { className: "text-sm font-medium mb-2", children: l.preview }), _jsx("p", { className: "rounded-md bg-muted p-4 whitespace-pre-wrap break-words text-sm", dir: "auto", children: form.body.replace(/{{(\d+)}}/g, (all, n) => form.samples[Number(n) - 1] || all) })] })] }), _jsxs("div", { className: "flex justify-end gap-2 mt-5", children: [_jsx("button", { type: "button", className: control, disabled: busy, onClick: () => setForm(null), children: l.cancel }), _jsx("button", { className: primary, disabled: busy, children: busy ? l.loading : l.save })] })] })) : (_jsxs(_Fragment, { children: [view?.header && (_jsx("p", { className: "font-semibold whitespace-pre-wrap break-words mb-3", dir: "auto", children: view.header })), !view?.header && view?.headerFormat && (_jsx("p", { className: "text-sm text-muted-foreground mb-3", children: view.headerFormat })), _jsx("p", { className: "whitespace-pre-wrap break-words", dir: "auto", children: view?.body }), view?.footer && (_jsx("p", { className: "text-sm text-muted-foreground mt-3 whitespace-pre-wrap break-words", dir: "auto", children: view.footer })), view?.buttons?.map((text, index) => (_jsx("p", { className: "text-sm mt-2", dir: "auto", children: text }, `${index}:${text}`))), _jsx("button", { autoFocus: true, className: `${control} mt-5`, onClick: () => setView(null), children: l.close })] }))] }))] }));
}
